import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { api, setToken, clearToken, hasToken } from '@/lib/api-client';
import { config } from '@/lib/config';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  avatar: string | null;
  firmId: string;
  role: string;
  firm?: { id: string; name: string; tier: string };
}

export interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isDemoMode: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string, firmName: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  logout: () => void;
}

const DEMO_USER: AuthUser = {
  id: 'demo-user',
  email: 'demo@basandmore.com',
  name: 'Demo User',
  avatar: null,
  firmId: 'demo-firm',
  role: 'admin',
  firm: { id: 'demo-firm', name: 'Demo Firm', tier: 'professional' },
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function isDemoMode(): boolean {
  return config.demo.enabled;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const demoMode = isDemoMode();

  const fetchUser = useCallback(async () => {
    if (demoMode) {
      setUser(DEMO_USER);
      setIsLoading(false);
      return;
    }

    if (!hasToken()) {
      setIsLoading(false);
      return;
    }
    try {
      const data = await api.get<AuthUser>('/auth/me');
      setUser(data);
    } catch {
      clearToken();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, [demoMode]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchUser();
  }, [fetchUser]);

  // Sync auth state across browser tabs: if the token is cleared/changed in
  // another tab (e.g. logout), reflect it here instead of staying logged in.
  useEffect(() => {
    if (demoMode) return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== 'bnm-auth-token') return;
      if (e.newValue) {
        fetchUser();
      } else {
        setUser(null);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [demoMode, fetchUser]);

  const login = async (email: string, password: string) => {
    if (demoMode) {
      setUser(DEMO_USER);
      return;
    }
    const data = await api.post<{ token: string; user: AuthUser }>('/auth/login', { email, password });
    setToken(data.token);
    setUser(data.user);
  };

  const register = async (name: string, email: string, password: string, firmName: string) => {
    if (demoMode) {
      setUser(DEMO_USER);
      return;
    }
    const data = await api.post<{ token: string; user: AuthUser }>('/auth/register', {
      name,
      email,
      password,
      firmName,
    });
    setToken(data.token);
    setUser(data.user);
  };

  const forgotPassword = async (email: string) => {
    if (demoMode) return;
    await api.post('/auth/forgot-password', { email });
  };

  const resetPassword = async (token: string, password: string) => {
    if (demoMode) return;
    await api.post('/auth/reset-password', { token, password });
  };

  const logout = () => {
    clearToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        isDemoMode: demoMode,
        login,
        register,
        forgotPassword,
        resetPassword,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
